<template>
  <div class="flex flex-col h-full px-4 pt-4">
    <!-- Textarea 영역 - 남은 공간을 모두 차지 -->
    <div class="flex-1 flex flex-col min-h-0">
      <textarea
        class="w-full flex-1 resize-none"
        style="border:none; background: transparent;"
        v-model="localText"
        :style="useAiTranslateFullScreen ? { maxHeight: '32vh' } : {}"
        @input="handleTextInput"
        @focusout="handleTextFocusOut"
      ></textarea>
    </div>

    <!-- TTS 버튼 또는 오디오 플레이어 영역 -->
    <div class="flex-shrink-0">
      <!-- 음성 생성 중인 경우 로딩 인디케이터 표시 -->
      <div v-if="localText && isGeneratingAudio" class="mb-4 w-full">
        <div class="flex items-center justify-center p-3 rounded-lg" style="background: #F5F5F5;">
          <feather-icon icon="LoaderIcon" class="animate-spin w-4 h-4 mr-2" style="color: #7367F0;" />
          <span class="text-sm" style="color: #6B7280;">음성 생성 중...</span>
        </div>
      </div>

      <!-- 번역된 음성이 있는 경우 브라우저 기본 오디오 플레이어 표시 -->
      <div v-else-if="localText && translatedAudioUrl" class="mb-4 w-full">
        <audio
          ref="audioPlayer"
          controls
          class="w-full"
          :src="audioBlobUrl || translatedAudioUrl"
          preload="metadata"
          @error="handleAudioError"
          @loadedmetadata="handleAudioLoaded"
        >
          브라우저가 오디오를 지원하지 않습니다.
        </audio>
      </div>

      <!-- 번역된 음성이 없고 텍스트만 있는 경우 TTS 버튼 표시 -->
      <div v-else-if="localText && !translatedAudioUrl" class="mb-4 flex">
        <feather-icon
          icon="Volume2Icon"
          class="inline-block w-4 h-4 mr-1 cursor-pointer transition-colors duration-200"
          :style="ttsButtonStyle"
          @click="handleTTSClick"
          :title="ttsTooltip"
        />
      </div>
    </div>


    <!-- 마이크 권한 안내 팝업 -->
    <div v-if="showPermissionGuide" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" @click="showPermissionGuide = false">
      <div class="bg-white rounded-lg p-6 m-4 max-w-md" @click.stop>
        <div class="flex items-center mb-4">
          <feather-icon icon="MicOffIcon" class="w-6 h-6 text-red-500 mr-3"/>
          <h3 class="text-lg font-semibold">마이크 권한이 필요합니다</h3>
        </div>
        <p class="text-gray-600 mb-4">
          음성 인식 기능을 사용하려면 마이크 접근 권한이 필요합니다.<br>
          "다시 시도" 버튼을 클릭하여 권한을 허용해주세요.
        </p>
        <div class="text-sm text-gray-500 mb-4">
          <p><strong>권한이 차단된 경우 해결 방법:</strong></p>
          <ul class="list-disc list-inside mt-2 space-y-1">
            <li>브라우저 주소창 왼쪽의 자물쇠 아이콘을 클릭하세요</li>
            <li>마이크 권한을 '허용'으로 변경하세요</li>
            <li>페이지를 새로고침하거나 "다시 시도"를 클릭하세요</li>
          </ul>
        </div>
        <div class="flex justify-end space-x-2">
          <button
            @click="showPermissionGuide = false"
            class="px-4 py-2 text-gray-500 hover:text-gray-700"
          >
            취소
          </button>
          <button
            @click="retryMicrophonePermission"
            class="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            다시 시도
          </button>
        </div>
      </div>
    </div>

    <!-- 하단 고정 버튼 영역 - 언어선택, 텍스트 번역, 녹음 번역 -->
    <div class="flex-shrink-0 pb-4">
      <div class="grid gap-2"
           :style="isMobile ? {gridTemplateColumns: '64px 1fr 64px'} : {gridTemplateColumns: '1fr 64px 64px'}">
        <div class="flex justify-center items-center order-3 md:order-1">
          <select v-model="currentLang" @change="handleLangChange" style="border:none; background: transparent;">
            <option v-for="item in locales" :value="item.lang">{{ item.name }}</option>
          </select>
        </div>

        <div
          @click="requestTextTranslation"
          class="rounded-full order-2 cursor-pointer"
          :class="{ 'opacity-50 cursor-not-allowed': isTranslating || !localText || !localText.trim() }"
          style="background: #00000099; width: 64px; height: 64px; display: flex; justify-content: center; align-items: center;"
        >
          <feather-icon
            :icon="isTranslating ? 'LoaderIcon' : 'Edit3Icon'"
            class="inline-block"
            :class="{ 'animate-spin': isTranslating }"
            style="color:#fff;"
          />
        </div>
        <div v-if="isRecording" @click="stopRecording"
             class="p-2 rounded-full order-4"
             style="background: #7367F0; width: 64px; height: 64px; display: flex; justify-content: center; align-items: center; border: 7px solid #BFCAFF"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V5C21 3.89543 20.1046 3 19 3Z" fill="white"/>
          </svg>
        </div>
        <div v-else-if="isTranslating"
             class="order-4 p-5 rounded-full"
             style="background: #ffa500; width: 64px; height: 64px; display: flex; justify-content: center; align-items: center;"
        >
          <feather-icon icon="LoaderIcon" class="inline-block animate-spin" style="color:#fff;"/>
        </div>
        <div v-else
             @click="handleMicButtonClick"
             class="order-4 p-5 rounded-full"
             :class="isMicrophoneEnabled && !isTranslating ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'"
             :style="isMicrophoneEnabled && !isTranslating ? 'background: #00000099; width: 64px; height: 64px; display: flex; justify-content: center; align-items: center;' : 'background: #cccccc; width: 64px; height: 64px; display: flex; justify-content: center; align-items: center;'"
        >
          <feather-icon :icon="isMicrophoneEnabled ? 'MicIcon' : 'MicOffIcon'" class="inline-block" :style="isMicrophoneEnabled ? 'color:#fff;' : 'color:#666;'"/>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as UiUtil from '@/util/UiUtil';
export default {
  props: {
    text: String,
    lang: String,
    isTranslating: Boolean,
    isGeneratingAudio: Boolean,  // TTS 음성 생성 중 여부
    useAiTranslateFullScreen: Boolean,
    translatedAudioUrl: String  // 번역된 음성 파일 URL
  },
  computed: {
    isMobile() {
      const size = this.$viewport.screens
      return size.indexOf(this.$viewport.screen) <= size.indexOf('sm');
    },
    isMicrophoneEnabled() {
      return this.micPermissionStatus === 'granted';
    },
    // TTS 지원 여부
    isTTSSupported() {
      if (!window.speechSynthesis) return false;

      // 현재 언어에 대한 TTS 지원 여부 확인
      const voices = this.availableVoices;
      if (voices.length === 0) return false;

      // 현재 언어코드와 매칭되는 음성이 있는지 확인
      const currentLangCode = this.currentLang.split('-')[0]; // 'ko-KR' → 'ko'
      const hasMatchingVoice = voices.some(voice => {
        const voiceLang = voice.lang.split('-')[0];
        return voiceLang === currentLangCode;
      });

      return hasMatchingVoice;
    },
    // TTS 버튼 스타일
    ttsButtonStyle() {
      if (this.isTTSSupported) {
        return { color: '#7E72F2' }; // 지원되는 경우 보라색
      } else {
        return { color: '#9CA3AF' }; // 지원되지 않는 경우 회색
      }
    },
    // TTS 툴팁 텍스트
    ttsTooltip() {
      if (this.isTTSSupported) {
        return '텍스트를 음성으로 읽기';
      } else {
        return `${this.getCurrentLanguageName()} 언어는 음성 합성을 지원하지 않습니다`;
      }
    }
  },
  watch: {
    // prop text가 변경되면 localText 업데이트
    text(newVal) {
      this.localText = newVal || '';
      this.newText = newVal;
    },
    lang(newVal) {
      if (newVal && newVal !== this.currentLang) {
        this.currentLang = newVal;
      }
    },
    // 번역된 음성 URL이 변경되면 인증된 오디오 로드
    async translatedAudioUrl(newUrl, oldUrl) {
      if (newUrl) {
        // 기존 오디오 정리
        this.cleanupAudioBlob();

        // 새로운 오디오 로드
        await this.loadAuthenticatedAudio();
      } else {
        console.log('번역 음성 URL 제거됨');
        this.cleanupAudioBlob();
      }
    }
  },
  data() {
    return {
      localText: this.text || '', // prop의 로컬 복사본
      newText: '',
      currentLang: this.lang || 'ko-KR',
      isRecording: false,
      recognition: null,
      mediaRecorder: null,
      recordedChunks: [],
      micPermissionStatus: 'unknown', // 'granted', 'denied', 'prompt', 'unknown'
      showPermissionGuide: false,
      audioBlobUrl: null, // 인증된 오디오 파일의 Blob URL
      isLoadingAudio: false, // 오디오 로딩 중 플래그
      audioLoadingPromise: null, // 현재 로딩 중인 Promise
      availableVoices: [], // 사용 가능한 TTS 음성 목록
      locales: [
        { name: '한국어', lang: 'ko-KR' },                             // Korean
        { name: 'বাংলা(방글라데시)', lang: 'bn-BD' },                  // Bengali
        { name: 'English(영어)', lang: 'en-US' },                     // English
        { name: 'Filipino(필리핀)', lang: 'fil-PH' },                 // Filipino
        { name: 'Français(프랑스어)', lang: 'fr-FR' },                 // French
        { name: 'हिन्दी(인도)', lang: 'hi-IN' },                        // Hindi (※ 인도 추가)
        { name: 'Bahasa Indonesia(인도네시아어)', lang: 'id-ID' },     // Indonesian
        { name: '日本語(일본어)', lang: 'ja-JP' },                      // Japanese
        { name: 'ភាសាខ្មែរ(캄보디아)', lang: 'km-KH' },                // Khmer
        { name: 'Кыргызча(키르기스어)', lang: 'ky-KG' },               // Kyrgyz
        { name: 'ລາວ(라오스)', lang: 'lo-LA' },                        // Lao
        { name: 'Bahasa Melayu(말레이시아)', lang: 'ms-MY' },          // Malay
        { name: 'Монгол(몽골어)', lang: 'mn-MN' },                      // Mongolian
        { name: 'မြန်မာ(미얀마)', lang: 'my-MM' },                     // Burmese
        { name: 'नेपाली(네팔어)', lang: 'ne-NP' },                     // Nepali
        { name: 'Português(동티모르)', lang: 'pt-TL' },                // Portuguese (East Timor)
        { name: 'Русский(러시아)', lang: 'ru-UZ' },               // Russian (※ 우즈벡에서 자주 사용)
        { name: 'සිංහල(스리랑카)', lang: 'si-LK' },                    // Sinhala
        { name: 'தமிழ்(스리랑카)', lang: 'ta-LK' },                    // Tamil (Sri Lanka)
        { name: 'Türkçe(투르크멘어)', lang: 'tk-TM' },                  // Turkmen
        { name: 'Tiếng Việt(베트남어)', lang: 'vi-VN' },               // Vietnamese
        { name: 'Oʻzbekcha(우즈베크어)', lang: 'uz-UZ' },              // Uzbek
        { name: 'اردو(파키스탄)', lang: 'ur-PK' },                     // Urdu
        { name: '简体中文(중국어간체)', lang: 'zh-CN' },                 // Simplified Chinese
        { name: '繁體中文(중국어번체)', lang: 'zh-TW' },                 // Traditional Chinese
        { name: 'ไทย(태국어)', lang: 'th-TH' }                         // Thai
      ]
    }
  },
  async mounted() {
    await this.checkMicrophonePermission();
    await this.loadTTSVoices();

    // 번역된 음성 URL이 이미 있는 경우 로드
    if (this.translatedAudioUrl) {
      this.loadAuthenticatedAudio();
    }
  },
  methods: {
    async checkMicrophonePermission() {
      try {
        // navigator.permissions API를 사용하여 권한 상태 확인
        if (navigator.permissions && navigator.permissions.query) {
          const permissionStatus = await navigator.permissions.query({ name: 'microphone' });
          this.micPermissionStatus = permissionStatus.state;

          // 권한 상태 변경 감지
          permissionStatus.onchange = () => {
            this.micPermissionStatus = permissionStatus.state;
          };
        } else {
          // navigator.permissions가 지원되지 않는 경우 미디어 접근을 통해 확인
          await this.requestMicrophoneAccess();
        }
      } catch (error) {
        this.micPermissionStatus = 'unknown';
      }
    },

    async requestMicrophoneAccess() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        this.micPermissionStatus = 'granted';
        // 스트림을 즉시 종료하여 리소스 절약
        stream.getTracks().forEach(track => track.stop());
      } catch (error) {
        if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
          this.micPermissionStatus = 'denied';
          this.showPermissionGuide = true;
        } else if (error.name === 'NotFoundError') {
          this.micPermissionStatus = 'denied';
          UiUtil.alertDialog('마이크가 연결되어 있지 않습니다.');
        } else {
          this.micPermissionStatus = 'unknown';
        }
      }
    },

    async retryMicrophonePermission() {
      this.showPermissionGuide = false;

      try {
        // 권한을 직접 요청
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        this.micPermissionStatus = 'granted';
        stream.getTracks().forEach(track => track.stop());
      } catch (error) {
        if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
          this.micPermissionStatus = 'denied';
          // 권한이 여전히 거부된 경우 다시 안내 표시
          setTimeout(() => {
            this.showPermissionGuide = true;
          }, 1000);
        }
      }
    },

    async showMicrophonePermissionGuide() {
      // 먼저 권한을 직접 요청해보기
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        this.micPermissionStatus = 'granted';
        stream.getTracks().forEach(track => track.stop());
        return;
      } catch (error) {
        // 권한 요청이 실패하면 오류 유형에 따라 다른 처리
        if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
          // 사용자가 권한을 거부한 경우 안내 팝업 표시
          this.showPermissionGuide = true;
        } else if (error.name === 'NotFoundError') {
          // 마이크가 없는 경우
          UiUtil.alertDialog(`마이크 장치를 찾을 수 없습니다.

확인 사항:
1. 마이크가 PC에 연결되어 있는지 확인
2. Windows 설정 > 시스템 > 소리 > 입력에서 마이크 인식 여부 확인
3. 마이크 드라이버가 정상 설치되어 있는지 확인
4. 다른 앱에서 마이크를 사용 중인지 확인

해결 후 다시 시도해주세요.`);
        } else {
          // 기타 오류
          this.showPermissionGuide = true;
        }
      }
    },

    handleMicButtonClick() {
      // 번역 중일 때는 클릭 무시
      if (this.isTranslating) {
        return;
      }

      if (this.isMicrophoneEnabled) {
        this.startContinuousRecognition();
      } else {
        this.showMicrophonePermissionGuide();
      }
    },

    // TTS 음성 목록 로드
    async loadTTSVoices() {
      if (!window.speechSynthesis) {
        console.warn('Speech Synthesis API를 지원하지 않는 브라우저입니다.');
        return;
      }

      return new Promise((resolve) => {
        // 음성 목록이 이미 로드된 경우
        let voices = window.speechSynthesis.getVoices();
        if (voices.length > 0) {
          this.availableVoices = voices;
          console.log('TTS 음성 목록 로드 완료:', voices.length, '개');
          resolve();
          return;
        }

        // 음성 목록이 아직 로드되지 않은 경우 이벤트 리스너 등록
        const loadVoices = () => {
          voices = window.speechSynthesis.getVoices();
          this.availableVoices = voices;
          console.log('TTS 음성 목록 로드 완료:', voices.length, '개');
          resolve();
        };

        window.speechSynthesis.addEventListener('voiceschanged', loadVoices, { once: true });

        // 일정 시간 후에도 로드되지 않으면 타임아웃
        setTimeout(() => {
          voices = window.speechSynthesis.getVoices();
          this.availableVoices = voices;
          console.log('TTS 음성 목록 타임아웃 로드:', voices.length, '개');
          resolve();
        }, 1000);
      });
    },

    // 현재 언어의 표시명 가져오기
    getCurrentLanguageName() {
      const locale = this.locales.find(l => l.lang === this.currentLang);
      return locale ? locale.name : this.currentLang;
    },

    // TTS 버튼 클릭 핸들러
    handleTTSClick() {
      if (this.isTTSSupported) {
        this.playTextToSpeech();
      } else {
        // 지원되지 않는 언어인 경우 알림 표시
        const languageName = this.getCurrentLanguageName();
        const message = `${languageName} 언어는 현재 브라우저에서 음성 합성을 지원하지 않습니다.`;

        if (this.$vs) {
          this.$vs.notify({
            title: 'TTS 지원 안됨',
            text: message,
            color: 'warning',
            icon: 'volume_off'
          });
        } else {
          UiUtil.alertDialog(message);
        }
      }
    },

    // 오디오 재생 메서드 - TTS만 사용 (번역된 음성은 브라우저 플레이어로 처리)
    playAudio() {
      this.playTextToSpeech();
    },

    // 브라우저 내장 TTS로 텍스트 읽기
    playTextToSpeech() {
      // 기존에 재생 중인 음성이 있으면 중단
      window.speechSynthesis.cancel();

      const msg = new SpeechSynthesisUtterance();
      msg.text = this.localText;
      msg.lang = this.currentLang;

      msg.onend = () => {
        console.log('TTS 재생 완료');
      };

      msg.onerror = (error) => {
        console.error('TTS 재생 실패:', error);
      };

      window.speechSynthesis.speak(msg);
    },


    handleLangChange() {
      this.$emit('lang', this.currentLang);
    },

    // 텍스트 입력 핸들러
    handleTextInput() {
      // 실시간으로 부모에게 알림 (필요한 경우)
      this.$emit('text', this.localText);
    },

    // 텍스트 포커스 아웃 핸들러
    handleTextFocusOut() {
      this.$emit('text', this.localText);
    },

    // 인증된 오디오 파일 로드 (중복 방지)
    async loadAuthenticatedAudio() {
      try {
        if (!this.translatedAudioUrl) {
          console.warn('번역된 오디오 URL이 없습니다.');
          return;
        }

        // 이미 로딩 중이면 기존 Promise 반환 (중복 방지)
        if (this.isLoadingAudio && this.audioLoadingPromise) {
          console.log('이미 오디오 로딩 중, 기존 Promise 사용');
          return await this.audioLoadingPromise;
        }

        console.log('인증된 오디오 로드 시작:', this.translatedAudioUrl);

        // 로딩 상태 설정
        this.isLoadingAudio = true;

        // 새로운 로딩 Promise 생성
        this.audioLoadingPromise = this._doLoadAuthenticatedAudio();

        const result = await this.audioLoadingPromise;

        return result;
      } catch (error) {
        console.error('인증된 오디오 로드 실패:', error);
        throw error;
      } finally {
        // 로딩 상태 해제
        this.isLoadingAudio = false;
        this.audioLoadingPromise = null;
      }
    },

    // 실제 로딩 작업 수행
    async _doLoadAuthenticatedAudio() {
      // Authorization이 필요한 URL인지 확인
      if (this.translatedAudioUrl.includes('/api/v1/file')) {
        console.log('Authorization 헤더가 필요한 URL로 인증된 요청 시작');

        // HttpRequest를 사용하여 Authorization 헤더가 포함된 요청
        const response = await Promise.race([
          this.$http.get(this.translatedAudioUrl, {
            responseType: 'blob',
            timeout: 30000  // 30초 타임아웃 명시적 설정
          }),
          new Promise((_, reject) => setTimeout(() => reject(new Error('HTTP 요청 타임아웃')), 30000))
        ]);

        if (response && response.data && response.data.size > 0) {
          // 기존 Blob URL 정리
          this.cleanupAudioBlob();

          // 새로운 Blob URL 생성
          const audioBlob = new Blob([response.data], { type: 'audio/mpeg' });
          this.audioBlobUrl = URL.createObjectURL(audioBlob);

          console.log('인증된 오디오 Blob URL 생성 완료:', this.audioBlobUrl, '크기:', audioBlob.size, 'bytes');

          // DOM 업데이트 대기
          await this.$nextTick();

          // 오디오 엘리먼트가 새 소스를 완전히 로드할 수 있도록 추가 대기
          await new Promise(resolve => setTimeout(resolve, 100));

          console.log('인증된 오디오 로딩 완료');
        } else {
          throw new Error('오디오 데이터를 받을 수 없거나 데이터가 비어있습니다.');
        }
      } else {
        // 일반 URL인 경우 직접 사용
        console.log('일반 URL로 직접 사용');
        this.audioBlobUrl = null;

        // DOM 업데이트 대기
        await this.$nextTick();
      }
    },

    // 오디오 Blob URL 정리
    cleanupAudioBlob() {
      if (this.audioBlobUrl) {
        console.log('오디오 Blob URL 정리:', this.audioBlobUrl);
        URL.revokeObjectURL(this.audioBlobUrl);
        this.audioBlobUrl = null;
      }

      // 로딩 상태도 초기화
      this.isLoadingAudio = false;
      this.audioLoadingPromise = null;
    },

    // 오디오 엘리먼트가 재생 준비될 때까지 대기
    async waitForAudioReady() {
      return new Promise((resolve, reject) => {
        const audioElement = this.$refs.audioPlayer;
        if (!audioElement) {
          reject(new Error('오디오 엘리먼트를 찾을 수 없습니다.'));
          return;
        }

        // 현재 src 확인
        const currentSrc = audioElement.src || audioElement.currentSrc;
        console.log('오디오 readyState 확인 - src:', currentSrc, 'readyState:', audioElement.readyState);

        // src가 없으면 에러
        if (!currentSrc || currentSrc === '') {
          reject(new Error('오디오 src가 설정되지 않았습니다.'));
          return;
        }

        // 이미 준비된 경우 즉시 resolve
        if (audioElement.readyState >= 3) { // HAVE_FUTURE_DATA 이상
          console.log('오디오가 이미 준비됨 (readyState:', audioElement.readyState, ')');
          resolve();
          return;
        }

        console.log('오디오 준비 대기 중... (현재 readyState:', audioElement.readyState, ', src:', currentSrc, ')');

        let timeoutId;
        let isResolved = false;
        let checkIntervalId;

        const cleanup = () => {
          if (timeoutId) clearTimeout(timeoutId);
          if (checkIntervalId) clearInterval(checkIntervalId);
          audioElement.removeEventListener('canplay', handleCanPlay);
          audioElement.removeEventListener('canplaythrough', handleCanPlay);
          audioElement.removeEventListener('loadeddata', handleCanPlay);
          audioElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
          audioElement.removeEventListener('error', handleError);
        };

        const handleCanPlay = () => {
          if (isResolved) return;
          isResolved = true;
          console.log('오디오 재생 준비 완료 (readyState:', audioElement.readyState, ')');
          cleanup();
          resolve();
        };

        const handleLoadedMetadata = () => {
          console.log('오디오 메타데이터 로드 완료 (readyState:', audioElement.readyState, ')');
          // 메타데이터만으로는 재생 준비가 완료된 것은 아니므로 계속 대기
        };

        const handleError = (error) => {
          if (isResolved) return;
          isResolved = true;
          console.error('오디오 로드 중 에러:', error, 'error code:', (audioElement.error && audioElement.error.code), 'error message:', audioElement.error && audioElement.error.message);
          cleanup();
          reject(new Error(`오디오 로드 에러: ${(audioElement.error && audioElement.error.message) || 'Unknown error'}`));
        };

        // 이벤트 리스너 등록
        audioElement.addEventListener('canplay', handleCanPlay);
        audioElement.addEventListener('canplaythrough', handleCanPlay);
        audioElement.addEventListener('loadeddata', handleCanPlay);
        audioElement.addEventListener('loadedmetadata', handleLoadedMetadata);
        audioElement.addEventListener('error', handleError);

        // readyState 주기적 체크 (디버깅용)
        checkIntervalId = setInterval(() => {
          if (isResolved) return;
          console.log('오디오 readyState 체크:', audioElement.readyState, '네트워크 상태:', audioElement.networkState);
        }, 1000);

        // 타임아웃 설정 (10초로 증가)
        timeoutId = setTimeout(() => {
          if (isResolved) return;
          isResolved = true;
          cleanup();
          console.warn('오디오 로드 타임아웃, 현재 상태로 진행 (readyState:', audioElement.readyState, ', networkState:', audioElement.networkState, ')');

          if (audioElement.readyState >= 2) { // HAVE_CURRENT_DATA 이상이면 시도
            console.log('최소 필요한 데이터가 로드됨, 재생 시도');
            resolve();
          } else if (audioElement.readyState >= 1) { // 최소 메타데이터라도 로드됐으면 진행
            console.log('메타데이터만 로드됨, 재생 시도');
            resolve();
          } else {
            reject(new Error(`오디오 로드 타임아웃 (readyState: ${audioElement.readyState}, networkState: ${audioElement.networkState})`));
          }
        }, 10000);

        // 강제로 로드 시도 (필요한 경우)
        if (audioElement.readyState === 0) {
          console.log('readyState가 0이므로 강제 로드 시도');
          try {
            audioElement.load();
          } catch (loadError) {
            console.warn('강제 로드 실패:', loadError);
          }
        }
      });
    },

    // 오디오 로드 완료 이벤트
    handleAudioLoaded() {
      console.log('오디오 메타데이터 로드 완료');
    },

    // 오디오 에러 이벤트
    handleAudioError(event) {
      const audioElement = event.target;
      const errorCode = (audioElement.error && audioElement.error.code);
      const errorMessage = (audioElement.error && audioElement.error.message);

      console.error('오디오 로드/재생 오류:', {
        event,
        errorCode,
        errorMessage,
        src: audioElement.src,
        currentSrc: audioElement.currentSrc,
        readyState: audioElement.readyState,
        networkState: audioElement.networkState
      });

      // 에러 코드별 상세 로깅
      switch (errorCode) {
      case 1: // MEDIA_ERR_ABORTED
        console.error('오디오 로드가 중단됨');
        break;
      case 2: // MEDIA_ERR_NETWORK
        console.error('네트워크 오류로 오디오 로드 실패');
        break;
      case 3: // MEDIA_ERR_DECODE
        console.error('오디오 디코딩 오류');
        break;
      case 4: // MEDIA_ERR_SRC_NOT_SUPPORTED
        console.error('지원되지 않는 오디오 형식 또는 소스');
        break;
      default:
        console.error('알 수 없는 오디오 오류');
      }

      // 인증된 URL로 재시도 (단, 이미 재시도 중이 아닌 경우만)
      if (!this.audioBlobUrl && this.translatedAudioUrl && this.translatedAudioUrl.includes('/api/v1/file') && !this.isLoadingAudio) {
        console.log('인증된 오디오 로드 재시도 (에러 핸들러)');
        this.loadAuthenticatedAudio().catch(retryError => {
          console.error('에러 핸들러 재시도도 실패:', retryError);
        });
      } else if (this.isLoadingAudio) {
        console.log('이미 오디오 로딩 중이므로 재시도 건너뛰기');
      } else if (this.audioBlobUrl) {
        console.log('이미 Blob URL 존재하므로 재시도 불필요');
      }
    },

    // 브라우저 플레이어를 통한 자동 재생
    async playWithBrowserPlayer() {
      try {
        console.log('브라우저 플레이어 재생 시도 시작');

        // DOM 업데이트 대기
        await this.$nextTick();

        if (!this.$refs.audioPlayer) {
          console.error('오디오 플레이어 ref를 찾을 수 없음');
          throw new Error('오디오 플레이어를 찾을 수 없습니다.');
        }

        // Authorization이 필요한 URL인 경우 인증된 오디오 로딩 먼저 확인
        if (this.translatedAudioUrl && this.translatedAudioUrl.includes('/api/v1/file')) {
          console.log('인증이 필요한 오디오 URL 감지, 인증된 로딩 확인 중...');

          // 이미 로딩 중이면 완료까지 대기
          if (this.isLoadingAudio) {
            console.log('이미 오디오 로딩 중, 완료까지 대기...');
            await Promise.race([
              this.audioLoadingPromise || Promise.resolve(),
              new Promise((_, reject) => setTimeout(() => reject(new Error('로딩 대기 타임아웃')), 15000))
            ]);
          }

          // audioBlobUrl이 없는 경우에만 새로 로드
          if (!this.audioBlobUrl) {
            console.log('인증된 오디오 Blob URL이 없음, 새로 로드 시작');
            await Promise.race([
              this.loadAuthenticatedAudio(),
              new Promise((_, reject) => setTimeout(() => reject(new Error('인증된 오디오 로드 타임아웃')), 15000))
            ]);
          } else {
            console.log('인증된 오디오 Blob URL 이미 존재:', this.audioBlobUrl);
          }

          // 로딩 후에도 audioBlobUrl이 없으면 에러
          if (!this.audioBlobUrl) {
            throw new Error('인증된 오디오 로딩에 실패했습니다.');
          }

          console.log('인증된 오디오 Blob URL 준비 완료:', this.audioBlobUrl);
        }

        const audioElement = this.$refs.audioPlayer;
        console.log('오디오 엘리먼트 찾음, 현재 src:', audioElement.src);

        // 오디오가 재생 준비될 때까지 대기 (타임아웃 포함)
        console.log('오디오 재생 준비 대기 중...');
        await Promise.race([
          this.waitForAudioReady(),
          new Promise((_, reject) => setTimeout(() => reject(new Error('오디오 준비 타임아웃')), 15000))
        ]);

        // 재생 시작
        console.log('브라우저 플레이어에서 재생 시작');
        audioElement.currentTime = 0; // 처음부터 재생

        const playPromise = audioElement.play();

        if (playPromise !== undefined) {
          await Promise.race([
            playPromise,
            new Promise((_, reject) => setTimeout(() => reject(new Error('재생 시작 타임아웃')), 5000))
          ]);
          console.log('브라우저 플레이어 자동 재생 시작됨');
        }

        // 재생 완료까지 대기 (최대 30초)
        return new Promise((resolve, reject) => {
          let isResolved = false;

          const cleanup = () => {
            audioElement.removeEventListener('ended', handleEnded);
            audioElement.removeEventListener('error', handleError);
            if (timeoutId) clearTimeout(timeoutId);
          };

          const handleEnded = () => {
            if (isResolved) return;
            isResolved = true;
            cleanup();
            console.log('브라우저 플레이어 재생 완료');
            resolve();
          };

          const handleError = (error) => {
            if (isResolved) return;
            isResolved = true;
            cleanup();
            console.error('브라우저 플레이어 재생 중 오류:', error);
            reject(error);
          };

          audioElement.addEventListener('ended', handleEnded);
          audioElement.addEventListener('error', handleError);

          // 30초 타임아웃
          const timeoutId = setTimeout(() => {
            if (isResolved) return;
            isResolved = true;
            cleanup();
            console.warn('브라우저 플레이어 재생 타임아웃');
            resolve(); // 타임아웃이어도 성공으로 처리
          }, 30000);
        });

      } catch (error) {
        console.error('브라우저 플레이어 재생 실패:', error);
        throw error;
      }
    },

    // 녹음 완료 시 번역 요청
    handleRecordingComplete() {
      if (this.recordedChunks.length > 0) {
        // 녹음된 오디오 Blob 생성
        const audioBlob = new Blob(this.recordedChunks, { type: 'audio/webm' });

        // 부모 컴포넌트에 번역 요청
        this.$emit('translate', { audioBlob });

        console.log('녹음 완료, 번역 요청:', audioBlob);
      }
    },

    // 텍스트 번역 요청
    requestTextTranslation() {
      // 번역 중이거나 텍스트가 없으면 실행하지 않음
      if (this.isTranslating) {
        return;
      }

      if (!this.localText || !this.localText.trim()) {
        // 텍스트가 없을 때 사용자에게 알림
        if (this.$vs) {
          this.$vs.notify({
            title: '번역 불가',
            text: '번역할 텍스트를 입력해주세요.',
            color: 'warning'
          });
        } else {
          UiUtil.alertDialog('번역할 텍스트를 입력해주세요.');
        }
        return;
      }

      // 부모 컴포넌트에 텍스트 번역 요청
      this.$emit('translate', { text: this.localText.trim() });
      console.log('텍스트 번역 요청:', this.localText.trim());
    },
    stopRecording () {
      this.isRecording = false;

      // 음성 인식 중지
      if (this.recognition) {
        this.recognition.stop();
      }

      // MediaRecorder 중지
      if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
        this.mediaRecorder.stop();
      }
    },

    async startContinuousRecognition() {
      // 권한 확인
      if (!this.isMicrophoneEnabled) {
        this.showMicrophonePermissionGuide();
        return;
      }

      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SpeechRecognition) {
        UiUtil.alertDialog('이 브라우저는 음성 인식을 지원하지 않습니다.');
        return;
      }

      // 다시 한번 실시간으로 권한 확인 및 스트림 획득
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (error) {
        this.micPermissionStatus = 'denied';
        this.showMicrophonePermissionGuide();
        return;
      }

      this.isRecording = true;
      this.recordedChunks = [];

      // MediaRecorder 설정 (음성 파일 녹음용)
      try {
        this.mediaRecorder = new MediaRecorder(stream);
        this.mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            this.recordedChunks.push(event.data);
          }
        };

        this.mediaRecorder.onstop = () => {
          // 녹음 완료 시 번역 요청
          this.handleRecordingComplete();
          // 스트림 정리
          stream.getTracks().forEach(track => track.stop());
        };

        this.mediaRecorder.start();
      } catch (error) {
        console.error('MediaRecorder 시작 실패:', error);
      }

      // 음성 인식 설정
      this.recognition = new SpeechRecognition();
      this.recognition.lang = this.currentLang;
      this.recognition.interimResults = true; // 중간 결과도 받기
      this.recognition.continuous = true; // 연속 인식 활성화
      this.recognition.maxAlternatives = 1;

      let finalTranscript = '';
      let interimTranscript = '';

      this.recognition.onresult = (event) => {
        interimTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;

          if (event.results[i].isFinal) {
            finalTranscript += `${transcript  } `;
          } else {
            interimTranscript += transcript;
          }
        }

        this.localText = finalTranscript + interimTranscript;
        this.$emit('text', this.localText);
      };

      this.recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
      };

      this.recognition.onend = () => {
        console.log('Speech recognition ended.');
        // continuous가 true여도 때때로 종료될 수 있으므로 재시작
        if (this.isRecording) {
          setTimeout(() => {
            this.recognition.start();
          }, 100);
        }
      };

      this.recognition.start();
    }
  },
  beforeDestroy() {
    window.speechSynthesis.cancel();
    this.cleanupAudioBlob();

    // 로딩 상태 정리
    this.isLoadingAudio = false;
    this.audioLoadingPromise = null;
  }
}

</script>

<style scoped lang="scss">

</style>
